// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React from "react";
import { AutoSizer, List } from "react-virtualized";
import { FontIcon } from "@fluentui/react";
import {
    IAsset,
    AssetState,
    ISize,
    AssetLabelingState,
    IProject,
    IDeleteFileCommand,
    IProjectLabel,
    IAdHocGroupProject
} from "../../../../models/applicationState";
import {
    AssetPreview,
    ContentSource,
} from "../../common/assetPreview/assetPreview";
import { strings } from "../../../../common/strings";
import _, { debounce } from "lodash";
import { getCookieEmail, parseEndpointByType } from "../../common/jsonSchemaFormHelper";
import Confirm from "../../common/confirm/confirm";
import { getPrimaryRedTheme } from "../../../../common/themes";
import axios from 'axios';
import EditorSideBarItem from './editorSideBarItem';
import EditorSideBarItemQueued from "./editorSideBarItemQueued";
import EditorSideBarItemPosted from "./editorSideBarItemPosted";
import EditorSideBarItemCompleted from "./editorSideBarItemCompleted";

/**
 * Properties for Editor Side Bar
 * @member assets - Array of assets to be previewed
 * @member onAssetSelected - Function to call when asset from side bar is selected
 * @member selectedAsset - Asset initially selected
 * @member thumbnailSize - The size of the asset thumbnails
 */
export interface IEditorSideBarProps {
    assets?: IAsset[];
    onAssetSelected: (asset: IAsset) => void;
    onBeforeAssetSelected?: () => boolean;
    onAssetLoaded?: (asset: IAsset, ContentSource: ContentSource) => void;
    selectedAsset?: IAsset;
    thumbnailSize?: ISize;
    project?: IProject;
    onFileDelete?(command: IDeleteFileCommand);
    selectedPreviewAssets?: string[];
    addAssetPreviewToList?: (asset: IAsset) => void;
    removeAssetPreviewFromList?: (asset: IAsset) => void;
    refreshKey?: number;
    projectLabels: IProjectLabel[];
    adHocProjectGroup: IAdHocGroupProject;
    loadAdHocOcrProject: (requestId: string) => Promise<void>;
    selectedRequestId?: string;    
    modelProjects?: Array<{ key: string; val: string }>;
    onGenerateModelChanged: (key: string, val: string, checked: boolean) => void;
    loadAdHocOcrV3GroupProjectDocument?: (groupId: string, documentId: string, docPosition: number) => Promise<IProject>;
    groupId?: string;
    toggleProjectToPost?(projectId: string, fileName: string, selected: boolean, generateModel: boolean);
}

/**
 * State for Editor Side Bar
 * @member selectedAsset - Asset selected from side bar
 */
export interface IEditorSideBarState {
    scrollToIndex: number;
    assetToDelete: IAsset;
    filterState: string;        
    calculatedHeight: number;
    contentReady: boolean;
}

/**
 * @name - Editor Side Bar
 * @description - Side bar for editor page
 */
export default class EditorSideBar extends React.Component<
    IEditorSideBarProps,
    IEditorSideBarState
> {
    private confirmDelete: React.RefObject<Confirm>;
    private tbdAssets: IAsset[] = [];
    private isProcessing: boolean;

    constructor(props, context) {
        super(props, context);
        this.checkboxRowRenderer = this.checkboxRowRenderer.bind(this);
        this.addAssetToList = this.addAssetToList.bind(this);
        this.removeAssetFromList = this.removeAssetFromList.bind(this);
        this.getAssetsLabelingState = this.getAssetsLabelingState.bind(this);
        this.applyFilter = this.applyFilter.bind(this);
        this.deleteAssetOnSelect = this.deleteAssetOnSelect.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.processQueuedProjects = this.processQueuedProjects.bind(this);
        
        this.confirmDelete = React.createRef<Confirm>();

        this.isProcessing = false;
    }



    public state: IEditorSideBarState = {
        scrollToIndex: this.props.selectedAsset
            ? this.props.assets.findIndex(
                (asset) => asset.id === this.props.selectedAsset.id
            )
            : 0,
        assetToDelete: null,
        filterState: '',
        calculatedHeight: 0,
        contentReady: false
    };

    public async componentDidMount() {
        //if (this.props && this.props.project && this.props.project.id) {             
            //this.setState({
            //    assets: this.props.assets
            //}, async () => {
            //    await this.getAssetsLabelingState();
            //});
        //}        
        this.handleResize = debounce(this.handleResize, 100); // Debounce resize events
        window.addEventListener("resize", this.handleResize);        

        await this.processQueuedProjects();
    }

    componentWillUnmount(): void {
        window.removeEventListener("resize", this.handleResize);
    }

    private async getAssetsLabelingState() {
        
        const getAssetLabelPromise = (asset: IAsset) => {
            return new Promise((resolve, reject) => {
                axios.post(parseEndpointByType('GetAssetLabel'), { projectId: this.props.project.id, assetName: asset.name, assetId: asset.id })
                    .then(result => {
                        resolve(result);
                    });
            }) 
        }
        let allAssets = this.props.assets;
        //let updatedAssets: IAsset[] = [];
        //assets.forEach(async (asset) => {
        //    if (asset.labelingState && asset.labelingState === AssetLabelingState.TBD) {
        //        const assetLabelStateResult = await axios.post(parseEndpointByType('GetAssetLabel'), { projectId: this.props.project.id, assetName: asset.name });
        //        if (assetLabelStateResult && assetLabelStateResult.data && assetLabelStateResult.data.successful) {
        //            asset.labelingState = assetLabelStateResult.data.hatchetAiCompanyProjectLabel.labelingState;
        //        }
        //    }
        //});
        let getAssetLabelPromises = [];
        for (var i = 0; i < allAssets.length; i++) {
            let asset = allAssets[i];
            if (asset.labelingState && asset.labelingState === AssetLabelingState.TBD) {
                getAssetLabelPromises.push(getAssetLabelPromise(asset));
                //const assetLabelStateResult = await axios.post(parseEndpointByType('GetAssetLabel'), { projectId: this.props.project.id, assetName: asset.name });
                //if (assetLabelStateResult && assetLabelStateResult.data && assetLabelStateResult.data.successful) {
                //    asset.labelingState = assetLabelStateResult.data.hatchetAiCompanyProjectLabel.labelingState;
                //    //updatedAssets.push(asset);
                //}
            }            
        }
        const self = this;
        Promise.all(getAssetLabelPromises).then(results => {
            for (var j = 0; j < results.length; j++) {
                const result = results[j];
                if (result.data && result.data.successful) {
                    const { hatchetAiCompanyProjectLabel } = result.data;
                    let modifyAsset = self.props.assets.find(r => r.id === hatchetAiCompanyProjectLabel.assetId)
                    if (modifyAsset) {
                        modifyAsset.labelingState = hatchetAiCompanyProjectLabel.labelingState;
                        self.tbdAssets.push(modifyAsset);
                    }
                }                
            }
        });        
    }

    private async processQueuedProjects() {
        if (this.props.adHocProjectGroup && this.props.adHocProjectGroup.adhocGroupProjects && this.props.adHocProjectGroup.adhocGroupProjects.length > 0) {
            const queuedProjects = this.props.adHocProjectGroup.adhocGroupProjects.filter(r => r.status.toLowerCase() === 'queued');            
            if (queuedProjects.length > 0) {
                const projIndex = this.props.adHocProjectGroup.adhocGroupProjects.findIndex(r => r.project.id === queuedProjects[0].project.id);
                await this.props.loadAdHocOcrV3GroupProjectDocument(this.props.groupId, queuedProjects[0].project.id, projIndex);                   
            }
            this.isProcessing = true;
        }
    }

    private listRef: React.RefObject<List> = React.createRef();

    handleResize = () => {
        this.setState({ calculatedHeight: this.calculateHeight() });
    };

    calculateHeight = () => {
        const rowHeight = 50; // Height of each row
        const rowCount = this.props.adHocProjectGroup.adhocGroupProjects && this.props.adHocProjectGroup.adhocGroupProjects.length > 0 ? this.props.adHocProjectGroup.adhocGroupProjects.length : 0; // Number of rows 
        const maxHeight = 600; // Maximum height for the list
        const padding = 20; // Additional padding
    
        // Calculate the total height based on the number of rows
        const totalHeight = rowHeight * rowCount + padding;
    
        // Return the smaller of the total height or the maximum height
        return Math.min(totalHeight, maxHeight);
    };

    public render() {
        if (
            this.props.adHocProjectGroup &&
            this.props.adHocProjectGroup.adhocGroupProjects &&
            this.props.adHocProjectGroup.adhocGroupProjects.length > 0
        ) {
            return (
                <div
                    className="editor-page-sidebar-nav"
                    key={this.props.refreshKey}
                    style={{
                        height: "100vh", // Full viewport height
                        overflowY: "auto", // Enable vertical scrolling if content overflows
                        border: "1px solid #0078d4", // Blue border for the sidebar
                        borderRadius: "8px", // Rounded corners
                        padding: "10px", // Padding inside the container
                        backgroundColor: "#e6f4ff", // Light blue background for the sidebar
                        color: "#004578" // Optional: Light background color
                    }}
                >
                    <style>
                        {`
                        /* Custom scrollbar styles */
                        .editor-page-sidebar-nav::-webkit-scrollbar {
                            width: 12px; /* Increase scrollbar width */
                        }
                        .editor-page-sidebar-nav::-webkit-scrollbar-track {
                            background: #cce7ff; /* Light blue background for the track */
                            border-radius: 10px; /* Rounded corners for the track */
                        }
                        .editor-page-sidebar-nav::-webkit-scrollbar-thumb {
                            background: #0078d4; /* Blue color for the thumb */
                            border-radius: 10px; /* Rounded corners for the thumb */
                        }
                        .editor-page-sidebar-nav::-webkit-scrollbar-thumb:hover {
                            background: #005a9e; /* Darker blue on hover */
                        }
                        .ms-Checkbox {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                        }
                        .ms-Checkbox-text {
                            color: #004578 !important; /* Dark blue text for checkboxes */
                        }
                        `}
                    </style>
                    {this.props.adHocProjectGroup.adhocGroupProjects.map(
                        (adHocProject, index) => {
                            let isGenerateModelChecked = false;
                            if (adHocProject) {
                                if (this.props.modelProjects && this.props.modelProjects.length > 0) {
                                    const kvp = this.props.modelProjects.find(r => r.key === adHocProject.vendor);
                                    if (kvp) {
                                        isGenerateModelChecked = kvp.val === adHocProject.project.id;
                                    }
                                }
                            }
                            if (adHocProject.status.toLowerCase() === 'queued') {
                                return (
                                    <EditorSideBarItemQueued
                                        key={adHocProject.projectName}
                                        projectId={adHocProject.projectName}
                                        //style={style}
                                        //checkboxRowRenderer={this.checkboxRowRenderer}
                                        renderBadges={this.renderBadges}
                                        onAssetLoaded={this.props.onAssetLoaded}
                                        getAssetCssClassNames={this.getAssetCssClassNames}
                                        adHocProject={adHocProject}
                                        loadAdHocOcrProject={this.props.loadAdHocOcrProject}
                                        selectedRequestId={this.props.selectedRequestId}
                                        style={{
                                            display: "flex",
                                            flexDirection: "column", // Stack items vertically
                                            gap: "10px", // Add spacing between items
                                            padding: "10px",
                                            margin: "10px",
                                            border: "1px solid #ddd", // Softer border for a cleaner look
                                            borderRadius: "10px", // Rounded corners for a modern appearance
                                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Subtle shadow for depth
                                            backgroundColor: "#ffffff", // Clean white background
                                            overflowY: "auto", // Enable scrolling if content overflows
                                            height: "100%", // Full height of the parent container
                                            maxHeight: "80vh", // Limit the height to 80% of the viewport
                                        }}
                                        groupId={this.props.groupId}
                                    />
                                )                                    
                            } 
                            else if (adHocProject.status.toLowerCase() === 'posted') { 
                                return (
                                    <EditorSideBarItemPosted
                                        key={adHocProject.projectName}
                                        projectId={adHocProject.projectName}
                                        //style={style}
                                        //checkboxRowRenderer={this.checkboxRowRenderer}
                                        renderBadges={this.renderBadges}
                                        onAssetLoaded={this.props.onAssetLoaded}
                                        getAssetCssClassNames={this.getAssetCssClassNames}
                                        adHocProject={adHocProject}
                                        loadAdHocOcrProject={this.props.loadAdHocOcrProject}
                                        selectedRequestId={this.props.selectedRequestId}
                                        style={{
                                            display: "flex",
                                            flexDirection: "column", // Stack items vertically
                                            gap: "10px", // Add spacing between items
                                            padding: "10px",
                                            margin: "10px",
                                            border: "1px solid #ddd", // Softer border for a cleaner look
                                            borderRadius: "10px", // Rounded corners for a modern appearance
                                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Subtle shadow for depth
                                            backgroundColor: "#ffffff", // Clean white background
                                            overflowY: "auto", // Enable scrolling if content overflows
                                            height: "100%", // Full height of the parent container
                                            maxHeight: "80vh", // Limit the height to 80% of the viewport
                                        }}
                                        groupId={this.props.groupId}
                                    />
                                )                                    
                                
                            }
                            else if (adHocProject.status.toLowerCase() === 'completed') { 
                                return (
                                    <EditorSideBarItemCompleted
                                        key={adHocProject.projectName}
                                        projectId={adHocProject.projectName}
                                        //style={style}
                                        //checkboxRowRenderer={this.checkboxRowRenderer}
                                        renderBadges={this.renderBadges}
                                        onAssetLoaded={this.props.onAssetLoaded}
                                        getAssetCssClassNames={this.getAssetCssClassNames}
                                        adHocProject={adHocProject}
                                        loadAdHocOcrProject={this.props.loadAdHocOcrProject}
                                        selectedRequestId={this.props.selectedRequestId}
                                        style={{
                                            display: "flex",
                                            flexDirection: "column", // Stack items vertically
                                            gap: "10px", // Add spacing between items
                                            padding: "10px",
                                            margin: "10px",
                                            border: "1px solid #ddd", // Softer border for a cleaner look
                                            borderRadius: "10px", // Rounded corners for a modern appearance
                                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Subtle shadow for depth
                                            backgroundColor: "#ffffff", // Clean white background
                                            overflowY: "auto", // Enable scrolling if content overflows
                                            height: "100%", // Full height of the parent container
                                            maxHeight: "80vh", // Limit the height to 80% of the viewport
                                        }}
                                        groupId={this.props.groupId}
                                    />
                                )                                    
                                
                            }
                            else {
                                return (
                                    <EditorSideBarItem
                                        key={adHocProject.projectName}
                                        projectId={adHocProject.projectName}
                                        //style={style}
                                        onAssetClicked={this.onAssetClicked}
                                        deleteAssetOnSelect={this.deleteAssetOnSelect}
                                        download={this.download}
                                        //checkboxRowRenderer={this.checkboxRowRenderer}
                                        renderBadges={this.renderBadges}
                                        onAssetLoaded={this.props.onAssetLoaded}
                                        getAssetCssClassNames={this.getAssetCssClassNames}
                                        adHocProject={adHocProject}
                                        loadAdHocOcrProject={this.props.loadAdHocOcrProject}
                                        selectedRequestId={this.props.selectedRequestId}
                                        hasModel={adHocProject.hasModel} 
                                        hasRunningRequest={adHocProject.hasRunningRequest}
                                        isGenerateModelChecked={isGenerateModelChecked}
                                        onGenerateModelChanged={this.props.onGenerateModelChanged}
                                        toggleProjectToPost={this.props.toggleProjectToPost}
                                        style={{
                                            display: "flex",
                                            flexDirection: "column", // Stack items vertically
                                            gap: "10px", // Add spacing between items
                                            padding: "10px",
                                            margin: "10px",
                                            border: "1px solid #ddd", // Softer border for a cleaner look
                                            borderRadius: "10px", // Rounded corners for a modern appearance
                                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Subtle shadow for depth
                                            backgroundColor: "#ffffff", // Clean white background
                                            overflowY: "auto", // Enable scrolling if content overflows
                                            height: "100%", // Full height of the parent container
                                            maxHeight: "80vh", // Limit the height to 80% of the viewport
                                        }}
                                    />
                                )
                            }                            
                        })
                    }
                </div>
            );
        }
    
        return null; // Render nothing if there are no projects
    }

    public async componentDidUpdate(prevProps: IEditorSideBarProps) {
        // if (prevProps.thumbnailSize !== this.props.thumbnailSize) {
        //     this.listRef.current.recomputeRowHeights();
        // }       
        if (prevProps.adHocProjectGroup !== this.props.adHocProjectGroup) {
            this.setState({ calculatedHeight: this.calculateHeight() });
        }

        if (!this.isProcessing) {
            await this.processQueuedProjects();            
        }

        if (prevProps.adHocProjectGroup && 
            this.props.adHocProjectGroup &&
            prevProps.adHocProjectGroup.refreshKey !== this.props.adHocProjectGroup.refreshKey) {
            await this.processQueuedProjects();
        }
        
        // const {id} = this.props.adHocProject.project;
        // setInterval(() => {
        //     this.props.loadAdHocOcrV3GroupProjectDocument(this.props.groupId, id);
        // }, 30000);
    }

    public applyFilter(filterPreviewSelectedStatusKeys: string[], filterPreviewText: string) {
        
    }

    private getRowHeight = (width: number) => {
        return width / (4 / 3) + 76;
    };

    private selectAsset = (selectedAsset: IAsset): void => {
        if (this.props.assets && Object.keys(this.props.assets).length > 0) {
            const scrollToIndex = this.props.assets.findIndex(
                (asset) => asset.id === selectedAsset.id
            );
    
            if (scrollToIndex) {
                this.setState(
                    {
                        scrollToIndex: scrollToIndex as number
                    },
                    () => {
                        //this.listRef.current.forceUpdateGrid();
                    }
                );
            }        
        }        
    };

    private onAssetClicked = (asset: IAsset): void => {
        if (this.props.onBeforeAssetSelected) {
            if (!this.props.onBeforeAssetSelected()) {
                return;
            }
        }

        this.selectAsset(asset);
        this.props.onAssetSelected(asset);
    };

    private addAssetToList(event, asset: IAsset) {
        this.props.addAssetPreviewToList(asset);
        event.preventDefault();
        event.stopPropagation();
    }

    private removeAssetFromList(event, asset: IAsset) {
        this.props.removeAssetPreviewFromList(asset);
        event.preventDefault();
        event.stopPropagation();
    }

    private isAssetSelected = (assetId: string): boolean => {
        return this.props.selectedPreviewAssets.some(r => r === assetId);
    }

    private checkboxRowRenderer = (asset: IAsset): JSX.Element => {
        if (this.isAssetSelected(asset.id)) {
            return (
                <FontIcon
                    iconName="CheckboxComposite"
                    style={{
                        position: "absolute",
                        bottom: ".5em",
                        right: ".5em",
                        zIndex: 10,
                        color: "rgb(255,255,255)"
                    }}
                    onClick={(e) => this.removeAssetFromList(e, asset)}
                />
            )
        }
        else {
            return (
                <FontIcon
                    iconName="checkbox"
                    style={{
                        position: "absolute",
                        bottom: ".5em",
                        right: ".5em",
                        zIndex: 10,
                        color: "rgb(255,255,255)"
                    }}
                    onClick={(e) => this.addAssetToList(e, asset)}
                />
            )
        }
    }

    private rowRenderer = ({ key, index, style }): JSX.Element => {
        //const sortedData = this.props.adHocProjectGroup.adhocGroupProjects.slice().sort((a, b) => a.vendor.localeCompare(b.vendor));
        //const adHocProject = sortedData[index];
        const adHocProject = this.props.adHocProjectGroup.adhocGroupProjects[index];
        let isGenerateModelChecked = false;
        if (adHocProject) {
            if (this.props.modelProjects && this.props.modelProjects.length > 0) {
                const kvp = this.props.modelProjects.find(r => r.key === adHocProject.vendor);
                if (kvp) {
                    isGenerateModelChecked = kvp.val === adHocProject.project.id;
                }
            }
        }
        
        return (
            <EditorSideBarItem
                key={adHocProject.projectName}
                projectId={adHocProject.projectName}
                //style={style}
                onAssetClicked={this.onAssetClicked}
                deleteAssetOnSelect={this.deleteAssetOnSelect}
                download={this.download}
                //checkboxRowRenderer={this.checkboxRowRenderer}
                renderBadges={this.renderBadges}
                onAssetLoaded={this.props.onAssetLoaded}
                getAssetCssClassNames={this.getAssetCssClassNames}
                adHocProject={adHocProject}
                loadAdHocOcrProject={this.props.loadAdHocOcrProject}
                selectedRequestId={this.props.selectedRequestId}
                hasModel={adHocProject.hasModel} 
                hasRunningRequest={adHocProject.hasRunningRequest}
                isGenerateModelChecked={isGenerateModelChecked}
                onGenerateModelChanged={this.props.onGenerateModelChanged}
                toggleProjectToPost={this.props.toggleProjectToPost}
                style={{
                    display: "flex",
                    flexDirection: "column", // Stack items vertically
                    gap: "10px", // Add spacing between items
                    padding: "10px",
                    margin: "10px",
                    border: "1px solid #ddd", // Softer border for a cleaner look
                    borderRadius: "10px", // Rounded corners for a modern appearance
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Subtle shadow for depth
                    backgroundColor: "#ffffff", // Clean white background
                    overflowY: "auto", // Enable scrolling if content overflows
                    height: "100%", // Full height of the parent container
                    maxHeight: "80vh", // Limit the height to 80% of the viewport
                }}
            />            
        );
        
    };

    private download(url, name, event) {
        fetch(url).then(async (t) => {
            const b = await t.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(b);
            a.setAttribute("download", name);
            a.click();
        });

        event.preventDefault();
        event.stopPropagation();
    }

    private deleteAssetOnSelect(event, asset: IAsset) {
        this.setState({
            assetToDelete: asset,
        });
        this.confirmDelete.current.open({});
        event.preventDefault();
        event.stopPropagation();
    }

    private async deleteAssetClick() {
        this.props.onFileDelete({
            email: getCookieEmail(),
            fileName: this.state.assetToDelete.name,
            // tslint:disable-next-line:radix
            projectId: parseInt(this.props.project.id),
        } as IDeleteFileCommand);
    }

    private renderBadges = (asset: IAsset): JSX.Element => {

        const getBadgeTaggedClass = (state: AssetLabelingState): string => {
            return state ? `badge-tagged-${AssetLabelingState[state]}` : "";
        };
        const getBadgeTaggedIcon = (labelingState: AssetLabelingState) => {            
            switch (labelingState) {
                case AssetLabelingState.AutoLabeled:
                    return <FontIcon iconName="AutoEnhanceOn" />;
                case AssetLabelingState.AutoLabeledAndAdjusted:
                    return <FontIcon iconName="AutoEnhanceOff" />;
                case AssetLabelingState.Trained:
                    return <FontIcon iconName="MachineLearning" />;
                default:
                    return <FontIcon iconName="Tag" />;
            }
        };
        let labelingState = _.capitalize(
            _.lowerCase(AssetLabelingState[asset.labelingState])
        );
        if (asset.labelingState == AssetLabelingState.TBD) {
            labelingState = '...';
        }
        let readFromAsset = false;
        if (this.props.projectLabels && this.props.projectLabels.length > 0) {
            const assetLabel = this.props.projectLabels.find(r => r.assetName.toLowerCase() === asset.name.toLowerCase());
            if (assetLabel) {
                labelingState = _.capitalize(
                    _.lowerCase(AssetLabelingState[assetLabel.labelingState])
                );
                switch (assetLabel.assetState) {
                    case AssetState.Tagged:
                        let labelingStateComponent = getBadgeTaggedIcon(assetLabel.labelingState);
                        return (
                            <span
                                title={labelingState}
                                className={[
                                    "badge",
                                    "badge-tagged",
                                    getBadgeTaggedClass(assetLabel.labelingState),
                                ].join(" ")}
                            >
                                {labelingStateComponent}
                            </span>
                        );
                    case AssetState.Visited:
                        return (
                            <span
                                title={strings.editorPage.visited}
                                className="badge badge-visited"
                            >
                                <FontIcon iconName="View" />
                            </span>
                        );
                    default:
                        return null;
                }
            }
            else {
                readFromAsset = true;
            }
        }
        else {
            readFromAsset = true;
        }
        if (readFromAsset) {
            switch (asset.state) {
                case AssetState.Tagged:
                    let labelingStateComponent = getBadgeTaggedIcon(asset.labelingState);
                    return (
                        <span
                            title={labelingState}
                            className={[
                                "badge",
                                "badge-tagged",
                                getBadgeTaggedClass(asset.labelingState),
                            ].join(" ")}
                        >
                            {labelingStateComponent}
                        </span>
                    );
                case AssetState.Visited:
                    return (
                        <span
                            title={strings.editorPage.visited}
                            className="badge badge-visited"
                        >
                            <FontIcon iconName="View" />
                        </span>
                    );
                default:
                    return null;
            }
        }
    };

    private getAssetCssClassNames = (
        asset: IAsset,
        selectedAsset: IAsset = null
    ): string => {
        const cssClasses = ["asset-item"];
        if (selectedAsset && selectedAsset.id === asset.id) {
            cssClasses.push("selected");
        }

        return cssClasses.join(" ");
    };
}
